<template>
  <v-card>
    <v-card-title class="primaryDialogTitle">
      <span class="headline">{{ isEdit ? 'Edit Patient' : 'Add Patient' }}</span>
      <v-spacer></v-spacer>
      <v-btn icon @click="close">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text>
      <ValidationObserver ref="form" v-slot="{ invalid }">
        <v-container>
          <!-- Patient Name Section -->
          <v-row>
            <v-col :cols="12" :md="6" class="pt-0">
              <custom-label item-field-name="*Last Name" />
              <ValidationProvider v-slot="{ errors }" name="Last Name" rules="required">
                <FletrixTextField
                  v-model="formData.name_last"
                  hide-details
                  placeholder="e.g., Doe"
                ></FletrixTextField>
                <span class="redRemind">{{ errors[0] }}</span>
              </ValidationProvider>
            </v-col>

            <v-col :cols="12" :md="6" class="pt-0">
              <custom-label item-field-name="*First Name" />
              <ValidationProvider v-slot="{ errors }" name="First Name" rules="required">
                <FletrixTextField
                  v-model="formData.name_first"
                  hide-details
                  placeholder="e.g., John"
                ></FletrixTextField>
                <span class="redRemind">{{ errors[0] }}</span>
              </ValidationProvider>
            </v-col>
          </v-row>

          <!-- Birthday -->
          <v-row>
            <v-col cols="12" md="12">
              <custom-label item-field-name="Birthday" />
              <v-menu
                ref="menu"
                v-model="menu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="formattedBirthday"
                    placeholder="DD/MM/YYYY"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    hide-details
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="formData.birthday"
                  @input="menu = false"
                  no-title
                  scrollable
                ></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>

          <!-- Contact Number -->
          <v-row>
            <v-col cols="12" md="12">
              <custom-label item-field-name="*Contact Number" />
              <FletrixPhone ref="phoneValidator" v-model="formData.contactNumber" />
            </v-col>
          </v-row>
        </v-container>
      </ValidationObserver>
    </v-card-text>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn text @click="close">Cancel</v-btn>
      <v-btn color="primary" :loading="loading" :disabled="invalid" @click="save">
        {{ isEdit ? 'Update' : 'Add' }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { FletrixTextField, FletrixPhone } from '@/components/Atom';
import CustomLabel from '../customFormLabel/customLabel';

export default {
  name: 'PatientForm',
  components: {
    ValidationObserver,
    ValidationProvider,
    FletrixTextField,
    FletrixPhone,
    CustomLabel,
  },
  props: {
    patientData: {
      type: Object,
      default: null,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      formData: {
        name_first: '',
        name_last: '',
        birthday: null,
        contactNumber: '',
      },
      menu: false,
    };
  },
  computed: {
    invalid() {
      return (
        !this.formData.name_first ||
        !this.formData.name_last ||
        !this.formData.contactNumber ||
        this.formData.contactNumber.length < 5
      );
    },
    formattedBirthday() {
      if (!this.formData.birthday) return '';
      const date = new Date(this.formData.birthday);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    },
  },
  watch: {
    patientData: {
      immediate: true,
      deep: true,
      handler() {
        this.loadPatientData();
      },
    },
  },
  methods: {
    loadPatientData() {
      if (this.patientData) {
        this.formData.name_first = this.patientData.name_first || '';
        this.formData.name_last = this.patientData.name_last || '';
        this.formData.birthday = this.patientData.birthday || null;
        this.formData.contactNumber = this.patientData.contactNumber || '';
      }
    },

    async save() {
      const formValid =
        this.$refs.form && this.$refs.form.validate ? await this.$refs.form.validate() : false;
      const phoneValid =
        this.$refs.phoneValidator && this.$refs.phoneValidator.$refs.form
          ? await this.$refs.phoneValidator.$refs.form.validate()
          : false;

      if (!formValid || !phoneValid) {
        return;
      }

      const payload = {
        name_first: this.formData.name_first,
        name_last: this.formData.name_last,
        contactNumber: this.formData.contactNumber,
      };

      if (this.formData.birthday) {
        payload.birthday = this.formData.birthday;
      }

      this.$emit('save', payload);
    },

    close() {
      this.$emit('close');
    },
  },
};
</script>

<style scoped lang="scss">
.redRemind {
  position: absolute;
  color: red;
  font-size: 12px;
}
</style>
